import { usePageRefreshKey } from '../../../../context/PageRefreshContext';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getFixedAssets, deleteFixedAsset } from '../../../../redux/actions/SocietyPortal/FixedAssetAction/FixedAssetAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { financialYears } from '../../../../consts/DropDownOptions';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_ACCOUNTS_FIXED_ASSETS_ADD, SOCIETY_PORTAL_ACCOUNTS_FIXED_ASSETS_EDIT } from '../../../../Routing/routes';

const getCurrentFY = () => {
    const now = new Date();
    const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${year}-${year + 1}`;
};

const FixedAssets = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const societyId = selectedSocietyStorer.getSocietyId();
    const { fixedAssetsData, loading, deleteLoading } = useSelector(state => state.FixedAssetReducer);

    const [financialYear, setFinancialYear] = useState(getCurrentFY());
    const [search, setSearch] = useState('');

    const assets = Array.isArray(fixedAssetsData) ? fixedAssetsData : fixedAssetsData?.data || [];

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (societyId) {
            dispatch(getFixedAssets({ societyId, year: financialYear }));
        }
    }, [dispatch, societyId, financialYear, refreshKey]);

    const filteredAssets = useMemo(() => {
        if (!search.trim()) return assets;
        const q = search.toLowerCase();
        return assets.filter(a =>
            a.name?.toLowerCase().includes(q)
        );
    }, [assets, search]);

    const handleEdit = (id) => {
        navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_ACCOUNTS_FIXED_ASSETS_EDIT.replace(':id', id));
    };

    const handleDelete = (id) => {
        if (!window.confirm('Are you sure you want to delete this asset?')) return;
        dispatch(deleteFixedAsset({
            societyId,
            id,
            onSuccess: () => dispatch(getFixedAssets({ societyId, year: financialYear })),
        }));
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            {/* Header */}
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-mb-5">
                <div className="tw-flex tw-items-center tw-gap-2.5">
                    <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-gradient-to-br tw-from-indigo-500 tw-to-purple-600 tw-flex tw-items-center tw-justify-center tw-shadow-md">
                        <Icon icon="hugeicons:building-06" className="tw-text-white tw-text-base" />
                    </div>
                    <div>
                        <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Fixed Assets</h1>
                        <p className="tw-text-[11px] tw-text-gray-400 tw-mt-0.5">
                            Manage and track your society's fixed assets
                        </p>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <SearchableDropdown
                        value={financialYear}
                        onChange={setFinancialYear}
                        options={financialYears}
                        placeholder="Financial Year"
                        small
                    />
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_ACCOUNTS_FIXED_ASSETS_ADD)}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-medium tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 tw-transition-colors tw-shadow-sm"
                    >
                        <Icon icon="mdi:plus" className="tw-text-sm" />
                        Add Fixed Asset
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="tw-mb-4">
                <div className="tw-relative tw-max-w-xs">
                    <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search assets..."
                        className="tw-w-full tw-pl-8 tw-pr-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-lg focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gradient-to-r tw-from-slate-50 tw-to-gray-50">
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">#</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Asset Name</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Purchase Date</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Debit Account</th>
                                <th className="tw-text-right tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Purchase Cost</th>
                                <th className="tw-text-center tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="tw-divide-y tw-divide-gray-50">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="tw-text-center tw-py-12 tw-text-gray-400">
                                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                            <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-blue-500"></div>
                                            <span className="tw-text-xs">Loading assets...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredAssets.length > 0 ? (
                                filteredAssets.map((asset, index) => {
                                    const purchase = asset.purchases?.[0];
                                    const totalAmount = parseFloat(purchase?.total_amount) || 0;
                                    const purchaseDate = purchase?.purchase_date || asset.acquisition_date || '';
                                    const debits = asset.journals?.[0]?.debits || [];
                                    const primaryDebit = debits.length > 0 ? debits.reduce((min, d) => (d.id < min.id ? d : min), debits[0]) : null;
                                    return (
                                        <tr key={asset.id || index} className="hover:tw-bg-slate-50/50 tw-transition-colors">
                                            <td className="tw-px-4 tw-py-2.5 tw-text-gray-400">{index + 1}</td>
                                            <td className="tw-px-4 tw-py-2.5 tw-font-medium tw-text-gray-800">{asset.name}</td>
                                            <td className="tw-px-4 tw-py-2.5 tw-text-gray-500">{purchaseDate}</td>
                                            <td className="tw-px-4 tw-py-2.5 tw-text-gray-600">
                                                {primaryDebit ? `${primaryDebit.glAccount?.ac_code} - ${primaryDebit.glAccount?.name}` : '-'}
                                            </td>
                                            <td className="tw-px-4 tw-py-2.5 tw-text-right tw-font-medium tw-text-gray-700">₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                            <td className="tw-px-4 tw-py-2.5 tw-text-center">
                                                <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                                                    <button
                                                        onClick={() => handleEdit(asset.id)}
                                                        className="tw-p-1 tw-rounded hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600 tw-transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Icon icon="mdi:pencil-outline" className="tw-text-sm" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(asset.id)}
                                                        disabled={deleteLoading}
                                                        className="tw-p-1 tw-rounded hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-500 tw-transition-colors disabled:tw-opacity-50"
                                                        title="Delete"
                                                    >
                                                        <Icon icon="mdi:trash-can-outline" className="tw-text-sm" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={6} className="tw-text-center tw-py-12">
                                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                            <Icon icon="hugeicons:building-06" className="tw-text-3xl tw-text-gray-300" />
                                            <p className="tw-text-xs tw-text-gray-400">No fixed assets found</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default FixedAssets;
